/// <reference types="vite/client" />
/**
 * Chain explorer URL helpers — keeps tx/block links chain-aware so the same
 * portal binary works against ADI testnet, mainnet, or local Hardhat.
 *
 * Reads `VITE_BLOCKCHAIN_CHAIN_ID` (and an optional `VITE_BLOCKCHAIN_EXPLORER`
 * override) at build time. Falls back to ADI testnet (chain id 99999).
 */

interface ExplorerConfig {
  base: string;
  /** Path prefix for transactions, e.g. `/tx/`. */
  txPath: string;
  /** Path prefix for blocks, e.g. `/block/`. */
  blockPath: string;
  /** Path prefix for addresses. */
  addressPath: string;
  label: string;
}

const REGISTRY: Record<string, ExplorerConfig> = {
  // ADI mainnet (current)
  '36900': {
    base: 'https://explorer.adifoundation.ai',
    txPath: '/tx/',
    blockPath: '/block/',
    addressPath: '/address/',
    label: 'ADI Mainnet',
  },
  // ADI mainnet (legacy chain id)
  '8400': {
    base: 'https://explorer.ab.adifoundation.ai',
    txPath: '/tx/',
    blockPath: '/block/',
    addressPath: '/address/',
    label: 'ADI Mainnet',
  },
  // ADI testnet
  '99999': {
    base: 'https://explorer.ab.testnet.adifoundation.ai',
    txPath: '/tx/',
    blockPath: '/block/',
    addressPath: '/address/',
    label: 'ADI Testnet',
  },
  // Local Hardhat
  '31337': {
    base: '',
    txPath: '#tx-',
    blockPath: '#block-',
    addressPath: '#addr-',
    label: 'Hardhat (local)',
  },
};

function resolveConfig(): ExplorerConfig {
  const override = import.meta.env.VITE_BLOCKCHAIN_EXPLORER as string | undefined;
  const chainId = String(import.meta.env.VITE_BLOCKCHAIN_CHAIN_ID ?? '99999');
  const fromRegistry = REGISTRY[chainId] ?? REGISTRY['99999'];
  if (override) {
    return { ...fromRegistry, base: override.replace(/\/$/, '') };
  }
  return fromRegistry;
}

export function getChainLabel(): string {
  return resolveConfig().label;
}

export function txUrl(txHash: string | null | undefined): string | null {
  if (!txHash) return null;
  const cfg = resolveConfig();
  if (!cfg.base) return null;
  return `${cfg.base}${cfg.txPath}${txHash}`;
}

export function blockUrl(block: number | null | undefined): string | null {
  if (block == null) return null;
  const cfg = resolveConfig();
  if (!cfg.base) return null;
  return `${cfg.base}${cfg.blockPath}${block}`;
}

export function addressUrl(address: string | null | undefined): string | null {
  if (!address) return null;
  const cfg = resolveConfig();
  if (!cfg.base) return null;
  return `${cfg.base}${cfg.addressPath}${address}`;
}

/** Truncate a hash for display: `0xabcd…1234`. */
export function shortHash(hash: string | null | undefined, head = 6, tail = 4): string {
  if (!hash) return '';
  if (hash.length <= head + tail + 3) return hash;
  return `${hash.slice(0, head)}…${hash.slice(-tail)}`;
}
